import type { RefObject } from "react";
import { useCallback, useEffect } from "react";
import {
    usePreventScrollBounceHook,
    type UsePreventScrollBounceHookOptions,
} from "./usePreventScrollBounceHook";

/** Configuration for {@link useOverlayScrollIsolationHook}. */
export interface UseOverlayScrollIsolationHookOptions extends UsePreventScrollBounceHookOptions {

    /**
     * When true, wheel events that hit a scroll boundary call `preventDefault`
     * so scroll chaining does not reach the map underneath.
     * @defaultValue true
     */
    readonly "preventWheelScrollChaining"?: boolean;
}

/**
 * Isolates a scrollable overlay from the page behind it.
 *
 * - Touch: delegates to {@link usePreventScrollBounceHook} to block rubber-band at edges.
 * - Wheel: stops propagation and optionally blocks scroll chaining at top/bottom.
 *
 * @param elementRef React ref pointing to the scrollable element.
 * @param options Hook configuration options.
 */
export const useOverlayScrollIsolationHook = <ElementType extends HTMLElement>(
    elementRef: RefObject<ElementType | null>,
    options: UseOverlayScrollIsolationHookOptions = {},
): void => {
    const {
        isEnabled = true,
        preventWheelScrollChaining = true,
    } = options;

    usePreventScrollBounceHook( elementRef, { isEnabled } );

    const handleWheel = useCallback(
        ( event: WheelEvent ) => {
            if ( !isEnabled ) {
                return;
            }

            const element = elementRef.current;

            if ( element === null ) {
                return;
            }

            event.stopPropagation();

            if ( !preventWheelScrollChaining ) {
                return;
            }

            if ( element.scrollHeight <= element.clientHeight ) {
                event.preventDefault();

                return;
            }

            const isAtTop = element.scrollTop <= 0;
            const isAtBottom =
                element.scrollTop + element.clientHeight >= element.scrollHeight - 1;

            if ( ( isAtTop && event.deltaY < 0 ) || ( isAtBottom && event.deltaY > 0 ) ) {
                event.preventDefault();
            }
        },
        [ elementRef, isEnabled, preventWheelScrollChaining ],
    );

    useEffect( () => {
        const element = elementRef.current;

        if ( element === null || !isEnabled ) {
            return;
        }

        element.addEventListener( "wheel", handleWheel, { "passive": false } );

        return () => {
            element.removeEventListener( "wheel", handleWheel );
        };
    }, [ elementRef, handleWheel, isEnabled ] );
};
